import { Box, Chip } from '@mui/material';
import { makeStyles , withStyles } from 'tss-react/mui';
import React from 'react';
import { ArrowForwardIos, VideocamOutlined } from '@mui/icons-material';
import { useTimezone } from '@uplift/contexts';
import { formatUserFullName } from '@uplift/utils';
import { Appointment, AppointmentRecurring } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { PatientStatus } from '@uplift/types';
import { Button } from '../../../../../Button';
import { LoadableTypography } from '../../../../../LoadableTypography';
import { InfoRow } from '../InfoRow';
import { InfoSection } from '../InfoSection';
import { JoinSessionButton } from '../JoinSessionButton';
import { Link } from 'react-router-dom';
import { providerPatientRoute } from '@uplift/constants';
import { formatDateTimeRange } from './helpers';
import { SubmitSessionButton } from '../SubmitSessionButton';

const InfoChip = withStyles(Chip, theme => ({
  root: {
    color: theme.palette.info.dark,
    background: theme.palette.info.background,
  }
}));


const useStyles = makeStyles()(theme => ({
  userName: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.darkSlate.main,
    fontWeight: 600,
  },
  disabledLabel: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  }
}));


export type SectionGeneralProps = {
  loading: boolean;
  appointment?: Appointment;
  patientDetails?: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName: string;
    status: PatientStatus;
    email: string;
    phone: string;
  };
};


export const SectionGeneral: React.FC<SectionGeneralProps> = ({ loading, ...props }) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { classes } = useStyles();
  const sessionRange = React.useMemo(() => {
    if (!props.appointment?.fromDatetime) return undefined;
    return formatDateTimeRange(props.appointment.fromDatetime, props.appointment.toDatetime, timezone);
  }, [ props.appointment, timezone ]);

  const userName = React.useMemo(() => {
    if (!props.patientDetails) return undefined;
    return formatUserFullName({
      firstName: props.patientDetails.firstName,
      preferredFirstName: props.patientDetails.preferredName,
      lastName: props.patientDetails.lastName,
      fallback: props.patientDetails.email,
    });
  }, [ props.patientDetails ]);

  const recurringLabel = React.useMemo(() => {
    if (!props.appointment?.recurring) {
      return null;
    }
    if (props.appointment.recurring === AppointmentRecurring.none) {
      return null;
    }
    return l10n.appointments.recurring[props.appointment.recurring];
  }, [ props.appointment?.recurring, l10n ]);

  const showJoinSessionButton = [ 'initial', 'started', 'scheduled' ].includes(props.appointment?.state || '');

  return (
    <InfoSection>
      <Box display="flex" alignItems="flex-end">
        <Box display="flex" overflow="hidden" flex="1">
          <LoadableTypography variant={userName === props.patientDetails?.email ? 'h4' : 'h2'} width="300px" loading={loading} className={classes.userName} title={userName}>
            {userName}
          </LoadableTypography>
        </Box>
        <Box height="36px" display="flex" alignItems="center">
          <Button
            style={{ whiteSpace: 'nowrap' }}
            size="small"
            color="secondary"
            endIcon={<ArrowForwardIos />}
            component={Link}
            to={props.patientDetails?.id && providerPatientRoute.createLink({ patientId: props.patientDetails?.id })}
          >
            Open Chart
          </Button>
        </Box>
      </Box>
      <Box display="flex">
        <Box display="flex" flexDirection="column" flex="1">
          <InfoRow label={l10n.sessions.detailsModal.when} mt={2}>
            <Box display="flex" gap="16px">
              <LoadableTypography width="200px" loading={loading}>
                {sessionRange}
              </LoadableTypography>
              {recurringLabel && <InfoChip label={recurringLabel} size="small" />}
            </Box>
          </InfoRow>
          <Box display="flex" flex="1" justifyContent="space-between">
            <InfoRow label={l10n.sessions.detailsModal.where} mt={2}>
              <LoadableTypography width="170px" loading={loading}>
                {l10n.appointments.visitLocation.virtual}
              </LoadableTypography>
            </InfoRow>
            <Box display="flex" alignItems="flex-end">
              {showJoinSessionButton ? (
                <JoinSessionButton
                  appointment={props.appointment}
                  patient={props.patientDetails}

                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<VideocamOutlined />}
                  disabled={!sessionRange}
                />
              ) : (
                <SubmitSessionButton
                  appointment={props.appointment}
                  variant="contained"
                  color="secondary"
                  size="small"
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </InfoSection>
  );
};
